import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { Eye, EyeSlash, LockKey, User } from '@phosphor-icons/react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { apiRequest, errorMessage, jsonBody } from '../api/client'
import type { AuthenticatedUser } from '../api/types'
import { navigate } from '../app/navigation'
import { cacheVerifiedSession } from '../app/offlineSession'
import { queryClient } from '../app/queryClient'

const schema = z.object({
  username: z.string().trim().min(1, 'Vyplň uživatelské jméno.').max(100),
  password: z.string().min(1, 'Vyplň heslo.').max(256),
})

type LoginValues = z.infer<typeof schema>

export function LoginPage() {
  const [showPassword, setShowPassword] = useState(false)
  const { register, handleSubmit, formState: { errors } } = useForm<LoginValues>({
    resolver: zodResolver(schema),
    defaultValues: { username: '', password: '' },
  })
  const login = useMutation({
    mutationFn: (values: LoginValues) => apiRequest<AuthenticatedUser>('/auth/login', {
      method: 'POST',
      body: jsonBody(values),
    }),
    onSuccess: (user) => {
      cacheVerifiedSession(user)
      queryClient.setQueryData(['session'], user)
      navigate('/', true)
    },
  })

  return (
    <div className="login-page">
      <main className="login-stack">
        <img
          className="login-mascot"
          src="/assets/repinu-login.png"
          alt="Shiba Inu Shiro cvičí na hrazdě"
        />
        <section className="login-card" aria-labelledby="login-title">
          <p className="eyebrow">Osobní tréninkový deník</p>
          <h1 id="login-title">Přihlášení</h1>
          <p className="login-intro">Pokračuj ke svému plánu, výkonům a dnešnímu tréninku.</p>

          <form onSubmit={handleSubmit((values) => login.mutate(values))} noValidate>
            <label className="field-label" htmlFor="username">Uživatelské jméno</label>
            <div className={`input-shell${errors.username ? ' has-error' : ''}`}>
              <User size={20} aria-hidden="true" />
              <input id="username" autoComplete="username" autoFocus {...register('username')} />
            </div>
            {errors.username && <p className="field-error">{errors.username.message}</p>}

            <label className="field-label" htmlFor="password">Heslo</label>
            <div className={`input-shell${errors.password ? ' has-error' : ''}`}>
              <LockKey size={20} aria-hidden="true" />
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                {...register('password')}
              />
              <button
                type="button"
                className="input-action"
                onClick={() => setShowPassword((value) => !value)}
                aria-label={showPassword ? 'Skrýt heslo' : 'Zobrazit heslo'}
              >
                {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {errors.password && <p className="field-error">{errors.password.message}</p>}

            {login.isError && <div className="form-alert" role="alert">{errorMessage(login.error)}</div>}

            <button type="submit" className="button button--primary button--wide" disabled={login.isPending}>
              {login.isPending ? 'Přihlašuji…' : 'Přihlásit se'}
            </button>
          </form>
        </section>
      </main>
      <footer className="login-footer">
        <strong>RepInu</strong>
        <a href="https://www.shiroji.com" target="_blank" rel="noreferrer">Navrhl Shiroji</a>
      </footer>
    </div>
  )
}